import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Button from './Common/Button';
import EnquiryModal from './EnquiryModal';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isEnquiryModalOpen, setIsEnquiryModalOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const headerRef = useRef(null);
  const [headerHeight, setHeaderHeight] = useState(0);

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
  const openEnquiryModal = () => setIsEnquiryModalOpen(true);
  const closeEnquiryModal = () => setIsEnquiryModalOpen(false);

  const pages = ['about', 'academics', 'admissions', 'facilities', 'gallery', 'careers', 'contact'];

  // Handle scroll for shrinking header
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Measure header height
  useEffect(() => {
    if (headerRef.current) {
      setHeaderHeight(headerRef.current.offsetHeight);
    }
  }, [scrolled, isMenuOpen]);

  return (
    <>
      {/* Main Header */}
      <header
        ref={headerRef}
        className={`fixed top-0 left-0 w-full z-50 backdrop-blur-sm border-b border-slate-200 dark:border-slate-800 transition-all duration-300 ${
          scrolled ? 'bg-background-light dark:bg-background-dark py-2 lg:py-2' : 'bg-background-light/80 dark:bg-background-dark/80 py-4 lg:py-4'
        }`}
      >
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              {/* Logo */}
              <Link to="/" className="flex items-center gap-2">
                <img
                  src="/images/logo.png"
                  alt="Anestar Logo"
                  className={`object-contain transition-all duration-300 ${
                    scrolled ? 'max-h-12 lg:max-h-14' : 'max-h-16 lg:max-h-20'
                  }`}
                />
              </Link>

              {/* Desktop Navigation */}
              <nav className="hidden lg:flex items-center gap-4 lg:gap-6 text-sm leading-none">
                {pages.map((page) => {
                  const label = page === 'admissions' ? 'Admissions & Fees' : page.charAt(0).toUpperCase() + page.slice(1).replace('-', ' & ');
                  return (
                    <Link
                      key={page}
                      className="font-medium hover:text-primary dark:hover:text-primary transition-all duration-300 hover:scale-105 relative group"
                      to={`/${page}`}
                      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                    >
                      {label}
                      <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-primary transition-all duration-300 group-hover:w-full"></span>
                    </Link>
                  );
                })}
              </nav>
            </div>

            <div className="flex items-center gap-2">
              {/* Enquiry Button */}
              <Button variant="primary" className="hidden sm:inline-flex text-sm px-3 py-1.5" onClick={openEnquiryModal}>
                Enquire Now
              </Button>

              {/* Mobile Menu Button */}
              <button
                className="lg:hidden p-2 rounded-md hover:bg-slate-100 dark:hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-primary"
                onClick={toggleMenu}
              >
                <span className="material-symbols-outlined">menu</span>
              </button>
            </div>
          </div>

          {/* Mobile Navigation */}
          {isMenuOpen && (
            <div className="lg:hidden border-t border-slate-200 dark:border-slate-800 py-2">
              <nav className="flex flex-col gap-2">
                {pages.map((page) => {
                  const label = page === 'admissions' ? 'Admissions & Fees' : page.charAt(0).toUpperCase() + page.slice(1).replace('-', ' & ');
                  return (
                    <Link
                      key={page}
                      className="text-sm font-medium hover:text-primary dark:hover:text-primary transition-colors"
                      to={`/${page}`}
                      onClick={() => {
                        setIsMenuOpen(false);
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                    >
                      {label}
                    </Link>
                  );
                })}
              </nav>
            </div>
          )}
        </div>
      </header>

      {/* Spacer div to prevent content overlapping the fixed header */}
      <div style={{ height: headerHeight }} />

      {/* Enquiry Modal */}
      <EnquiryModal isOpen={isEnquiryModalOpen} onClose={closeEnquiryModal} />
    </>
  );
};

export default Header;
